import { useCookieStore } from '../store/cookieStore';

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';
// Dashboard links (toolbar, session replay, surveys) need the real PostHog
// UI host explicitly once api_host points at our own reverse-proxy domain
// instead of a *.posthog.com host — PostHog can no longer infer it.
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

let started = false;
let posthogModule: typeof import('posthog-js') | null = null;

async function start(): Promise<void> {
  if (started || !POSTHOG_KEY) return;
  started = true;
  // Dynamic import: the SDK's ~100KB+ is only fetched once a visitor has
  // actually granted analytics consent, instead of shipping in every page's
  // initial bundle whether or not they ever accept.
  posthogModule = await import('posthog-js');
  posthogModule.default.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: true,
    // Report uncaught exceptions and unhandled promise rejections as $exception
    // events, with the console log leading up to them attached — so a crash
    // (e.g. the viewer's WebGL/CORS panorama failure) shows up as an Error
    // Tracking issue with a stack trace instead of silently going unrecorded.
    capture_exceptions: true,
    // $web_vitals capture (LCP/CLS/INP/...) stopped mid-July — explicit here
    // rather than left to fall back on the project's remote config, which is
    // what silently regressed. network_timing stays off; it's tied to
    // Session Replay, which isn't enabled in this project.
    capture_performance: { web_vitals: true },
  });
}

/** Sends a custom event once PostHog is running (i.e. after analytics consent);
 * a silent no-op before that, so callers never need to check consent. */
export function captureEvent(name: string, props?: Record<string, unknown>): void {
  posthogModule?.default.capture(name, props);
}

/**
 * Wires PostHog to the existing cookie-consent store (browser only — never
 * called from the SSR entry). The SDK is never loaded/init'd — so it cannot
 * make any network request — until a visitor has actually accepted the
 * "Analytics" category in CookieConsent.tsx. Revoking consent afterwards
 * opts back out of an already-started session.
 */
export function initPostHog(): void {
  if (!POSTHOG_KEY) return; // no project key configured (e.g. local dev) — skip entirely

  if (useCookieStore.getState().preferences.analytics) start();

  useCookieStore.subscribe((state, prevState) => {
    if (state.preferences.analytics === prevState.preferences.analytics) return;
    if (state.preferences.analytics) start();
    else if (started) posthogModule?.default.opt_out_capturing();
  });
}
